import { AlertCircle } from "lucide-react";
import type { ParameterDescriptor, ParameterValue, ValidationError } from "@/types";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "./SearchableSelect";
import { toDateInputValue } from "@/lib/utils/date";
import { cn } from "@/lib/utils/cn";

interface ParameterControlProps {
  descriptor: ParameterDescriptor;
  value: ParameterValue;
  errors: ValidationError[];
  onChange: (value: ParameterValue) => void;
  disabled?: boolean;
}

/**
 * Dynamic control router. Renders the correct input for a parameter's resolved
 * control type and shows Apple-style inline validation underneath.
 */
export function ParameterControl({
  descriptor,
  value,
  errors,
  onChange,
  disabled,
}: ParameterControlProps) {
  const invalid = errors.length > 0;
  const describedBy = invalid ? `${descriptor.id}-error` : undefined;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={descriptor.id} required={descriptor.required}>
          {descriptor.name}
        </Label>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground/60">
          {descriptor.controlType.replace("-", " ")}
        </span>
      </div>

      {renderControl()}

      {invalid && (
        <p
          id={describedBy}
          className="flex items-center gap-1 text-xs text-destructive animate-fade-in"
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {errors[0].message}
        </p>
      )}
    </div>
  );

  function renderControl() {
    switch (descriptor.controlType) {
      case "boolean":
        return (
          <div className="flex h-8 items-center">
            <Switch
              id={descriptor.id}
              checked={Boolean(value)}
              onCheckedChange={(v) => onChange(v)}
              disabled={disabled}
            />
            <span className="ml-3 text-sm text-muted-foreground">
              {value ? "Enabled" : "Disabled"}
            </span>
          </div>
        );

      case "number":
        return (
          <Input
            id={descriptor.id}
            type="number"
            inputMode={descriptor.dataType === "int" ? "numeric" : "decimal"}
            value={value === null ? "" : String(value)}
            min={descriptor.range?.min as number | undefined}
            max={descriptor.range?.max as number | undefined}
            step={descriptor.range?.stepSize ?? (descriptor.dataType === "int" ? 1 : "any")}
            invalid={invalid}
            disabled={disabled}
            aria-describedby={describedBy}
            onChange={(e) =>
              onChange(e.target.value === "" ? null : Number(e.target.value))
            }
          />
        );

      case "date":
        return (
          <Input
            id={descriptor.id}
            type="date"
            value={toDateInputValue(value as string)}
            invalid={invalid}
            disabled={disabled}
            aria-describedby={describedBy}
            onChange={(e) => onChange(e.target.value || null)}
          />
        );

      case "datetime":
        return (
          <Input
            id={descriptor.id}
            type="datetime-local"
            value={value ? String(value).slice(0, 16) : ""}
            invalid={invalid}
            disabled={disabled}
            aria-describedby={describedBy}
            onChange={(e) =>
              onChange(e.target.value ? new Date(e.target.value).toISOString() : null)
            }
          />
        );

      case "list":
        return (
          <Select
            value={value === null ? undefined : String(value)}
            onValueChange={(v) => onChange(v)}
            disabled={disabled}
          >
            <SelectTrigger id={descriptor.id} invalid={invalid} aria-describedby={describedBy}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {descriptor.allowableValues?.map((o) => (
                <SelectItem key={String(o.value)} value={String(o.value)}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "dynamic-list":
      case "multi-select":
        return (
          <SearchableSelect
            value={value === null ? null : String(value)}
            options={descriptor.allowableValues ?? []}
            onChange={(v) => onChange(v)}
            invalid={invalid}
          />
        );

      case "text":
      default:
        return (
          <Input
            id={descriptor.id}
            type="text"
            value={value === null ? "" : String(value)}
            invalid={invalid}
            disabled={disabled}
            aria-describedby={describedBy}
            className={cn(invalid && "border-destructive")}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  }
}
